import { forwardRef, type ComponentProps, type ReactNode } from 'react';
import { Button as KitButton, type ButtonProps } from '@avala/ui/components/primitives/button';
import { cn } from '@avala/ui/lib/utils';
export { Input } from '@avala/ui/components/primitives/input';
export { Label } from '@avala/ui/components/primitives/label';
export { Textarea } from '@avala/ui/components/primitives/textarea';
import { Switch as KitSwitch } from '@avala/ui/components/primitives/switch';

export function Switch(props: ComponentProps<typeof KitSwitch>) {
  return <KitSwitch {...props} size="lg" className={cn('support-switch', props.className)} />;
}

// Support composes kit primitives; other products keep their own button treatment.
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, ...props },
  ref,
) {
  return (
    <KitButton
      ref={ref}
      className={cn(
        'min-h-11 min-w-11 rounded-lg text-sm normal-case tracking-normal shadow-none hover:shadow-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        className,
      )}
      {...props}
    />
  );
});

export function PageHeading({
  title,
  description,
  actions,
}: {
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <div className="support-page-heading">
      <div className="min-w-0 space-y-2">
        <h1 className="support-page-title">{title}</h1>
        <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">{description}</p>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function SettingSection({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`} className="support-setting-section scroll-mt-6">
      <div>
        <h2 id={`${id}-heading`} className="text-base font-medium">
          {title}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <div className="support-setting-controls">{children}</div>
    </section>
  );
}
